import React, { useMemo } from 'react'
import { SparqlEditor } from './SPARQLEditor'
import { Input, Label } from 'reactstrap'
import { FormGroup} from './FormGroup'
import AceEditor from 'react-ace'
// import MonacoEditor from 'react-monaco-editor';
// import brace from 'brace';

import styles from './FormField.module.css'
import widgetConfigStyles from './WidgetConfiguration.module.css'
import { ShowableComponent, useDialog } from "../hooks/use-dialog"
import { HelpDialog } from "./HelpDialog"
import { JsonEditor, JsonTextEditor } from "./JsonEditor"
import { CodeTextEditor } from "./code-text-editor"
import {mergeClasses} from '../helpers/styling'
import cssFix from './mui/resolutions.module.css'
import { SearchDropdown } from './ui-primitives/search-dropdown'
import {TablePPCustomConfigView} from './widgets/TablePlusPlus/components/CustomConfiguration'
import {type TablePlusPlusCustomConfig, DEFAULT_TABLEPP_CUSTOM_CONFIG} from './widgets/TablePlusPlus/config-schema'
import { TurtleEditor } from './widgets/ShaclForm/TurtleEditor'
import { ComponentConfig, SetComponentConfig } from './widgets/ComponentWidget/SetComponentConfig'
import { FilterConfig } from './widgets/FilterPlusPlus/filterplusplus-model'
import { FilterPlusPlusConfig } from './widgets/FilterPlusPlus/FilterPlusPlusConfig'
import { ConfigDescription, useMaybeWidgetId } from "../helpers/widget"
import { assertNever } from "../utils/assert-never"

type FormFieldProps = {
    name: string
    label: React.ReactNode
    topicValueInterpolationEnabled?: boolean
    placeholder?: string
    noResultsLabel?: string
    helpComponent?: ShowableComponent<{}, void>}
  & {setConfigFeedback?: SetConfigFeedback}
  & ( {type: 'array', value?: string[], onChange: (name: string, value: string[]) => void}
    | {type: 'boolean', value?: boolean, onChange: FormFieldOnChange<boolean>}
    | {type: 'javascript', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'json', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'json-text', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'number', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'select', value?: string, onChange: (name: string, value: string) => void, options: {label: string, value: string}[]}
    | {type: 'searchselect', value?: string, onChange: (name: string, value: string) => void, options: {label: string, value: string}[]}
    | {type: 'text', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'textarea', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'html', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'turtle', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'yasgui', value?: string, onChange: (name: string, value: string) => void}
    | {type: 'tablepp-custom', value?: TablePlusPlusCustomConfig,
       onChange: (name: string, value: TablePlusPlusCustomConfig) => void
       setConfigFeedback: SetConfigFeedback}
    | {type: 'filterpp-custom', value?: FilterConfig,
       onChange: (name: string, value: FilterConfig) => void}
    | {type: 'component-config', value: ComponentConfig,
       onChange: (name: string, value: ComponentConfig) => void}
    )

type FormFieldOnChange<V, O = void> = (name: string, value: V) => O

export type SetConfigFeedback = (type: 'valid' | 'warning' | 'error', message?: React.ReactNode) => void

function FormField(props: FormFieldProps): React.ReactNode {
  const id = `${props.name}-formfield`

  switch (props.type) {

    case 'yasgui': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} />
        <SparqlEditor
          mode="init-value"
          value={props.value ?? ''}
          onBlur={(value) => props.onChange(props.name, value)}
          id={`yasqe-widget-main-${props.name}`}
        />
      </FormGroup>
    )

    case 'html': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} for={id}
                       topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <CodeTextEditor
          name={props.name}
          value={props.value ?? ''}
          onValueChange={value => props.onChange(props.name, value)}
          mode="html"
          fileExtension="html"
          mimeType="text/html"
          collapsedHeight="360px"
        />
      </FormGroup>
    )

    case 'turtle': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} />
        {props.topicValueInterpolationEnabled &&
          <i className="justify-self-end">topic {"{{variables}}"} enabled</i>}
        <TurtleEditor
          value={props.value ?? ''}
          onChange={(value) => props.onChange(props.name, value)}
          placeholder={props.placeholder}
          minHeight="300px"
        />
      </FormGroup>
    )

    case 'javascript': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} />
        <AceEditor
          mode="javascript"
          theme="github"
          style={{ width: '100%', height: '120px' }}
          value={props.value}
          onChange={(newValue) => {
            props.onChange(props.name, newValue)
          }}
          name="editor"
        />
      </FormGroup>
    )

    case 'select': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} for={id} />
        <Input
          id={id}
          className={mergeClasses(styles.default, 'dark-form-control', cssFix['dark-form-control-select'])}
          value={props.value || ''}
          type="select"
          onChange={e => props.onChange(props.name, e.target.value)}
        >
          {props.options && props.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>))}
        </Input>
      </FormGroup>
    )

    case 'searchselect': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} for={id} />
        <SearchDropdown
          id={id}
          value={props.value ?? null}
          onChange={value => props.onChange(props.name, value || '')}
          options={props.options}
          labelConfig={{
            placeholder: props.placeholder || 'Search...',
            noOptionsFound: props.noResultsLabel || 'No options found.'
          }}
          classNames={{
            inputWrapper: "text-white bg-[#373a3c] !border-gray-400",
            input: "placeholder:text-gray-300",
            popover: "bg-none !border-1 !border-gray-400 jas jas overflow-hidden",
            list: "text-white bg-[#373a3c]",
            item: "data-[selected=true]:bg-indigo-400"
          }}
        />
      </FormGroup>
    )

    case 'boolean': return <BooleanField {...props} />

   case 'json': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} />
        <JsonEditor name={props.name}
                    value={props.value}
                    onValueChange={value => {
                      props.onChange(props.name, value as string)
                    }} />
      </FormGroup>
    )

    case 'json-text': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} />
        <JsonTextEditor name={props.name} value={props.value || ''}
                        onValueChange={value => {
                          props.onChange(props.name, value as string)
                        }} />
      </FormGroup>
    )

    case 'array': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent}
          topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <Input className="dark-form-control" value={props.value || ''}
          onChange={e => props.onChange(props.name, e.target.value.split(','))}
        />
      </FormGroup>
    )

    case 'tablepp-custom': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent}
          topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <TablePPCustomConfigView name={props.name}
          customConfig={props.value ?? DEFAULT_TABLEPP_CUSTOM_CONFIG}
          setConfigFeedback={props.setConfigFeedback}
          onChange={props.onChange} />
      </FormGroup>
    )

    case 'filterpp-custom': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent}
          topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <FilterPlusPlusConfig customConfig={props.value} onChange={v => props.onChange(props.name, v)}
        />
      </FormGroup>
    )

    case 'component-config': return (
      <FormGroup>
         <LabelWithHelp label={props.label} helpComponent={props.helpComponent}
           topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
         <SetComponentConfig
           value={props.value}
           onChange={value => props.onChange(props.name, value)} />
      </FormGroup>
    )

    case 'text': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} for={id}
          topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <Input className="dark-form-control" value={props.value || ''}
        id={id}
          onChange={e => props.onChange(props.name, e.target.value)}
        />
      </FormGroup>
    )

    case 'textarea': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent} for={id}
                       topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <textarea id={id}
                  style={{ fieldSizing: 'content' } as React.CSSProperties /* field-sizing-content in TW 4.+ */}
                  className="
                      dark-form-control w-full
                      py-1 px-2
                      border !border-gray-400 rounded-md
                      resize-y"
                  onChange={e => props.onChange(props.name, e.currentTarget.value)}>
          {props.value || ''}
        </textarea>
      </FormGroup>
    )

    case 'number': return (
      <FormGroup>
        <LabelWithHelp label={props.label} helpComponent={props.helpComponent}
          topicValueInterpolationEnabled={props.topicValueInterpolationEnabled} />
        <Input className="dark-form-control" value={props.value || ''}
          onChange={e => props.onChange(props.name, e.target.value)}
        />
      </FormGroup>
    )

    default:
      const anyProps: {[Property in keyof FormFieldProps]-?: any} = props
      console.error(`unknown type of formfield: ${anyProps.type}`)
      return (
        <FormGroup>
          <LabelWithHelp label={anyProps.label} helpComponent={anyProps.helpComponent}
            topicValueInterpolationEnabled={anyProps.topicValueInterpolationEnabled} />
          <Input className="dark-form-control"
            onChange={e => anyProps.onChange(anyProps.name, e.target.value)} />
        </FormGroup>
      )
  }
}

function BooleanField(props: Extract<FormFieldProps, { type: 'boolean' }>) {
  const show = useDialog()
  const widgetId = useMaybeWidgetId()
  return (
    <FormGroup>
      <div className="flex items-center gap-4">
        <Label className="cursor-pointer flex-grow mb-0">
          <Input
            checked={props.value ?? false}
            className={styles.check}
            type="checkbox"
            onChange={(e) =>
              props.onChange(props.name, e.target.checked)
            }
          />
          {'  '}
          {props.label}
        </Label>
        {props.helpComponent && (
          <button
            type="button"
            className="bg-transparent border-none cursor-pointer p-0 hover:text-blue-400 hover:underline"
            onClick={() => show(HelpDialog, { component: props.helpComponent!, widgetId })}
          >
            Help...
          </button>
        )}
      </div>
    </FormGroup>
  )
}

export function LabelWithHelp(
  props: {for?: string} & Pick<FormFieldProps, 'label' | 'topicValueInterpolationEnabled' | 'helpComponent'>
) {
  const show = useDialog()
  const helpComponent = props.helpComponent
  const widgetId = useMaybeWidgetId()

  return (
    <Label for={props.for ?? undefined} style={{ width: '100%', whiteSpace: 'pre-line' }}>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{ flexGrow: 1 }}>{props.label}</div>

        {props.topicValueInterpolationEnabled &&(
          <i>topic {"{{variables}}"} enabled</i>
        )}
        {helpComponent && (
          <a href="#" onClick={e => {
            e.preventDefault()
            show(HelpDialog, {component: helpComponent, widgetId})
          }}>
            Help...
          </a>
        )}
      </div>
    </Label>
  )
}

type FormItemPart<Item> = { type: "item", item: Item }
type FormGroupPart<Item> = { type: "group", legend: string, items: Item[] }
type FormPart<Item> = FormItemPart<Item> | FormGroupPart<Item>

export function FormFields<CD extends ConfigDescription<any, any, any, any>>(props: {
  descriptions: ReadonlyArray<CD> | null,
  values: Record<string, unknown>,
  onChange: (name: string, value: unknown) => void,
  setConfigFeedback: (description: CD, ...parms: Parameters<SetConfigFeedback>) => void,
}) {
  const parts = useMemo(
    () => props.descriptions ? createFormParts(props.descriptions) : [],
    [props.descriptions]
  )

  return parts.map((part) => {
    switch (part.type) {
      case "item":
        return renderField(part.item)
      case "group":
        return (
          <FormGroup tag="fieldset" className={widgetConfigStyles.fieldset} key={`fs-${part.legend}`}>
            <legend>{part.legend}</legend>
            {part.items.map(renderField)}
          </FormGroup>
        )
      default:
        assertNever(part)
    }
  })

  function renderField(variable: CD) {
    return (
      <FormField
        key={variable.name}
        name={variable.name}
        label={variable.label}
        type={variable.type}
        topicValueInterpolationEnabled={'topicValueInterpolationEnabled' in variable && variable.topicValueInterpolationEnabled}
        helpComponent={'helpComponent' in variable ? variable.helpComponent : undefined}
        options={'options' in variable ? variable.options : undefined}
        value={props.values[variable.name] as any}
        onChange={props.onChange}
        setConfigFeedback={(type, message) => props.setConfigFeedback(variable, type, message)}
      />
    )
  }
}

function createFormParts<CD extends ConfigDescription<any, any, any, any>>(
  descriptions: ReadonlyArray<CD>
): FormPart<CD>[] {
  const parts: FormPart<CD>[] = []
  const groupsByLegend = new Map<string, FormGroupPart<CD>>()

  for (const description of descriptions) {
    if (description.formGroup) {
      if (!groupsByLegend.has(description.formGroup)) {
        const group: FormGroupPart<CD> = {
          type: "group",
          legend: description.formGroup,
          items: []
        }
        groupsByLegend.set(description.formGroup, group)
        parts.push(group)
      }

      const group = groupsByLegend.get(description.formGroup)!
      group.items.push(description)
    } else {
      parts.push({ type: "item", item: description })
    }
  }

  return parts
}

export default FormField
